// frontend/src/components/LOS/Caprion/CaprionLoanVerification.tsx
//
// Caprion Loan Verification Page
// ===============================
//
// Dedicated page for polling loan status after AA consent is completed.
// This page is shown AFTER AA Consent page and BEFORE Loan Details page.
//
// Flow:
// 1. User completes AA consent on previous page
// 2. This page automatically starts loan status polling
// 3. Polls every 5 seconds for up to 10 minutes (120 attempts)
// 4. Handles 4 statuses:
//    - Pending: Continue polling (not terminal)
//    - Approved: Proceed to next page (TERMINAL SUCCESS)
//    - Rejected: Show rejection screen, block navigation (TERMINAL FAILURE)
//    - Withdrawn: Show rejection screen, block navigation (TERMINAL FAILURE)
//
// Backend updates LoanDetail with approved values automatically via
// fill_loan_detail_from_check_status function.

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useCaprionRejectionGuard } from '@/hooks/useCaprionRejectionGuard';
import CaprionRejectionScreen from './CaprionRejectionScreen';
import CaprionStatusLoader from './CaprionStatusLoader';
import { FileSearch, TrendingUp, CheckCircle2, Clock } from 'lucide-react';

interface CaprionLoanVerificationProps {
  onDone?: () => void;
  isBuilderMode?: boolean;
  registerSubmit?: (submitFn: () => Promise<boolean>) => void;
}

const CaprionLoanVerification: React.FC<CaprionLoanVerificationProps> = ({
  onDone,
  isBuilderMode = false,
  registerSubmit
}) => {
  // Check rejection status on page load (1st check - prevents unnecessary polling)
  const { isRejected: isRejectedOnLoad, rejectionReason: rejectionReasonOnLoad, rejectionTimestamp } = useCaprionRejectionGuard();
  
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [loanStatus, setLoanStatus] = useState<string | null>(null);
  
  // State for rejection from polling (2nd check - after status poll completes)
  const [pollingRejected, setPollingRejected] = useState(false);
  const [pollingRejectionReason, setPollingRejectionReason] = useState<string | null>(null);
  
  const [error, setError] = useState<string | null>(null);
  
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const hasStartedRef = useRef(false); // Flag to prevent duplicate calls
  const MAX_POLL_ATTEMPTS = 120; // 120 * 5 seconds = 10 minutes


  // Start polling on component mount (only once, even in StrictMode)
  // Skip polling if already rejected on page load
  useEffect(() => {
    if (!isBuilderMode && !hasStartedRef.current && !isRejectedOnLoad) {
      hasStartedRef.current = true;
      startPolling();
    }
    
    return () => {
      // Cleanup on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isRejectedOnLoad]);

  const startPolling = async () => {
    try {
      setIsPolling(true);
      setError(null);
      pollCountRef.current = 0;

      console.log('[LOAN VERIFICATION] Starting loan status poll (AA verification context)...');
      
      // Start loan status poll with AA verification context
      const startPollResponse = await axios.post(
        `${API_BASE_URL}/api/caprion/start-status-poll/`,
        { poll_context: 'aa_verification' },
        { withCredentials: true }
      );

      if (!startPollResponse.data.success) {
        throw new Error(startPollResponse.data.error || 'Failed to start status poll');
      }

      const taskId = startPollResponse.data.task_id;
      console.log('[LOAN VERIFICATION] Poll started, task_id:', taskId);

      // Start polling the task status
      const pollStatus = async () => {
        try {
          pollCountRef.current++;
          setPollCount(pollCountRef.current);
          console.log(`[LOAN VERIFICATION] Poll attempt ${pollCountRef.current}/${MAX_POLL_ATTEMPTS}`);

          const statusResponse = await axios.get(
            `${API_BASE_URL}/api/caprion/poll-status/${taskId}/`,
            { withCredentials: true }
          );

          if (statusResponse.data.success && statusResponse.data.is_ready) {
            const currentLoanStatus = statusResponse.data.result?.loan_status;
            setLoanStatus(currentLoanStatus);
            console.log(`[LOAN VERIFICATION] Polling complete, loan_status: ${currentLoanStatus}`);

            // Stop polling
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsPolling(false);

            // Handle each status
            
            // 1. Pending - Keep polling (should not reach here as is_ready would be false)
            if (currentLoanStatus === 'Pending') {
              console.log('[LOAN VERIFICATION] Status: Pending - continuing poll...');
              if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                throw new Error('Loan verification is taking longer than expected. Please try again later.');
              }
            }
            
            // 2. Approved OR Ops Verification - Proceed to next page (TERMINAL SUCCESS)
            else if (currentLoanStatus === 'Approved' || currentLoanStatus === 'Ops Verification') {
              console.log(`[LOAN VERIFICATION] ✅ Status: ${currentLoanStatus} - proceeding...`);
              
              // Log loan details if available
              const result = statusResponse.data.result;
              if (result?.data?.status) {
                const statusData = result.data.status;
                console.log('[LOAN VERIFICATION] Loan details:', {
                  amount: statusData.amount,
                  roi: statusData.roi,
                  tenure: statusData.tenure,
                  emi: statusData.emi
                });
              }
              
              // Proceed to next page after 1 second
              setTimeout(() => {
                if (onDone) {
                  onDone();
                }
              }, 1000);
            }
            
            // 3. Rejected - Show rejection screen (TERMINAL FAILURE)
            else if (currentLoanStatus === 'Rejected') {
              const backendMessage = statusResponse.data.result?.message || '';
              // Filter out technical messages that shouldn't be shown to users
              const reason = backendMessage.includes('Final status already captured')
                ? 'We regret to inform you that your loan application could not be approved based on the bank statement data shared through Account Aggregator. This may be due to insufficient transaction history, irregular cash flows, or other criteria not meeting the lender\'s requirements.'
                : backendMessage || 'We regret to inform you that your loan application could not be approved based on the bank statement data shared through Account Aggregator. This may be due to insufficient transaction history, irregular cash flows, or other criteria not meeting the lender\'s requirements.';
              console.log('[LOAN VERIFICATION] ❌ Application Rejected!');
              
              // Set rejection state to show rejection screen
              // Backend Celery task automatically updates DB (is_rejected, rejection_reason, rejection_timestamp)
              setPollingRejectionReason(reason);
              setPollingRejected(true);
            }
            
            // 4. Withdrawn - Show rejection screen (TERMINAL FAILURE)
            else if (currentLoanStatus === 'Withdrawn') {
              const backendMessage = statusResponse.data.result?.message || '';
              // Filter out technical messages that shouldn't be shown to users
              const reason = backendMessage.includes('Final status already captured')
                ? 'Your loan application has been withdrawn by the lender after reviewing your Account Aggregator data. Please contact support for more details.'
                : backendMessage || 'Your loan application has been withdrawn by the lender after reviewing your Account Aggregator data. Please contact support for more details.';
              console.log('[LOAN VERIFICATION] ❌ Application Withdrawn!');
              
              // Set rejection state to show rejection screen
              // Backend Celery task automatically updates DB (is_rejected, rejection_reason, rejection_timestamp)
              setPollingRejectionReason(reason);
              setPollingRejected(true);
            }
            
            // 5. Unknown status
            else {
              console.log(`[LOAN VERIFICATION] Unknown status: ${currentLoanStatus}`);
              if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                throw new Error('Loan verification timed out. Please try again later.');
              }
            }
          } else if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
            // Max attempts reached
            throw new Error('Loan verification is taking longer than expected. Please try again later.');
          }
        } catch (err: any) {
          console.error('[LOAN VERIFICATION] Poll error:', err);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          setError(err.message || 'Failed to verify loan status');
        }
      };

      // Start polling immediately, then every 5 seconds
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, 5000);

    } catch (err: any) {
      console.error('[LOAN VERIFICATION] Error starting poll:', err);
      setIsPolling(false);
      setError(err.message || 'Failed to start loan verification');
    }
  };

  // Register submit function (blocks navigation if rejected)
  useEffect(() => {
    if (registerSubmit) {
      // Block navigation if rejected (from page load check OR polling check)
      if (isRejectedOnLoad || pollingRejected) {
        registerSubmit(async () => false);
      } else {
        registerSubmit(async () => true);
      }
    }
  }, [registerSubmit, isRejectedOnLoad, pollingRejected]);

  // Builder mode preview
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🔄</div>
        <p className="font-semibold text-gray-500">Caprion: Loan Verification</p>
        <p className="text-xs mt-1">
          Polls loan status after AA consent. Handles: Pending (continue), Approved (proceed), Rejected/Withdrawn (block).
        </p>
      </div>
    );
  }

  // Show rejection screen if rejected (from page load check OR polling check)
  if (isRejectedOnLoad || pollingRejected) {
    return (
      <CaprionRejectionScreen
        reason={pollingRejected ? pollingRejectionReason || undefined : rejectionReasonOnLoad || undefined}
        timestamp={rejectionTimestamp || new Date().toISOString()}
        context="aa_verification"
      />
    );
  }

  // Custom steps for AA verification context
  const aaVerificationSteps = [
    { icon: FileSearch, text: 'Analysing your Account Aggregator data', duration: 2500 },
    { icon: TrendingUp, text: 'Generating best offers for you', duration: 3000 },
    { icon: Clock, text: 'Processing with lender', duration: 3500 },
    { icon: CheckCircle2, text: 'Verifying loan application status', duration: 4000 },
  ];

  // Show polling UI with custom AA verification steps
  return <CaprionStatusLoader title="AA Verification" customSteps={aaVerificationSteps} />;
};

export default CaprionLoanVerification;
